import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, ArrowLeft, AlertCircle, RefreshCw } from "lucide-react";
import { fetchProjectStages, fetchStemProjectById, fetchProjectProgress } from "../redux/stemThunk";
import { clearProjectStages } from "../redux/stemSlice";
import StemProjectWorkflow from "../components/StemProjectWorkflow";

export default function StemProjectWorkflowPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    stages = [],
    stagesLoading,
    stagesError,
    projects = [],
    selectedProject,
  } = useSelector((state) => state.stem || {});

  // Find project in redux state or fall back to selectedProject
  const project =
    projects.find((p) => String(p.id) === String(projectId)) ||
    (selectedProject && String(selectedProject.id) === String(projectId) ? selectedProject : null);

  useEffect(() => {
    if (projectId) {
      console.log("[StemProjectWorkflowPage] Fetching stages and progress via Redux for projectId:", projectId);
      dispatch(fetchProjectStages(projectId));
      dispatch(fetchProjectProgress(projectId));
      if (!project) {
        dispatch(fetchStemProjectById(projectId));
      }
    }
    return () => {
      dispatch(clearProjectStages());
    };
  }, [dispatch, projectId, project]);

  const handleBack = () => {
    navigate("/student-stem/projects");
  };

  const handleRetry = () => {
    if (projectId) {
      dispatch(fetchProjectStages(projectId));
      dispatch(fetchStemProjectById(projectId));
    }
  };

  // 1. Proper Loading State with Loader animation
  if (stagesLoading && stages.length === 0) {
    return (
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header Bar Skeleton */}
        <div
          style={{
            background: "var(--white)",
            borderRadius: "10px",
            border: "1px solid var(--line)",
            padding: "14px 18px",
            marginBottom: "14px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={handleBack}
              style={{
                background: "var(--bg)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "6px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "12px",
                fontWeight: "600",
                color: "var(--ink)",
              }}
            >
              <ArrowLeft size={14} />
              <span>Back</span>
            </button>
            <span style={{ fontSize: "14px", fontWeight: "700", color: "var(--ink)" }}>
              {project?.name || `Project #${projectId}`}
            </span>
          </div>

          <span
            style={{
              fontSize: "11px",
              color: "var(--blue-2)",
              fontWeight: "600",
              background: "var(--blue-soft)",
              border: "1px solid var(--green-soft)",
              padding: "3px 10px",
              borderRadius: "12px",
            }}
          >
            Fetching Stages...
          </span>
        </div>

        {/* Central Dedicated Loader Card */}
        <div
          style={{
            background: "var(--white)",
            borderRadius: "10px",
            border: "1px solid var(--line)",
            padding: "60px 20px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              background: "var(--blue-soft)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 14px",
              color: "var(--blue-2)",
            }}
          >
            <Loader2 size={26} style={{ animation: "spin 1s linear infinite" }} />
          </div>
          <h4 style={{ fontSize: "15px", fontWeight: "700", color: "var(--ink)", margin: "0 0 6px" }}>
            Loading Project Stages...
          </h4>
          <p style={{ fontSize: "12.5px", color: "var(--mute)", margin: 0 }}>
            Calling API for active stage workflow and questions
          </p>
        </div>
      </div>
    );
  }

  // 2. Error State
  if (stagesError && stages.length === 0) {
    return (
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            background: "var(--white)",
            borderRadius: "10px",
            border: "1px solid var(--red-soft)",
            padding: "32px 20px",
            textAlign: "center",
          }}
        >
          <AlertCircle size={32} color="var(--red)" style={{ margin: "0 auto 10px" }} />
          <h4 style={{ fontSize: "15px", fontWeight: "700", color: "var(--red-ink)", margin: "0 0 6px" }}>
            Failed to Load Stages
          </h4>
          <p style={{ fontSize: "13px", color: "var(--red-ink)", marginBottom: "16px" }}>
            {stagesError}
          </p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button
              type="button"
              onClick={handleBack}
              style={{
                background: "var(--bg)",
                border: "1px solid var(--line)",
                borderRadius: "6px",
                padding: "7px 14px",
                fontSize: "12.5px",
                fontWeight: "600",
                color: "var(--ink)",
                cursor: "pointer",
              }}
            >
              Back to Projects
            </button>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                background: "linear-gradient(var(--blue), var(--blue-2))",
                border: "none",
                borderRadius: "6px",
                padding: "7px 16px",
                fontSize: "12.5px",
                fontWeight: "700",
                color: "var(--white)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <RefreshCw size={13} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Loaded State: Render Workflow with project and stages
  const activeProjectData = {
    ...(project || { id: Number(projectId), name: `Project #${projectId}` }),
    stages,
  };

  return (
    <StemProjectWorkflow
      project={activeProjectData}
      onBack={handleBack}
    />
  );
}
